import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService, AlertType } from '../../shared/services/alert.service';
import { CommonModule } from '@angular/common';
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { MatSelectModule } from '@angular/material/select';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BookVersionResponse, BookVersionStruct } from '../../shared/services/responses/platform/book-version.response';
import { BookVersionService } from '../../shared/services/apis/platform/book-version.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatOptionModule } from '@angular/material/core';
import { BookDetailsService } from '../../shared/services/apis/platform/book-details.service';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { BookDetailsRequest } from '../../shared/services/requests/platform/book-details.request';
import { AgreementStruct } from '../../shared/services/requests/platform/upload-livro.request';
import { CategoryService } from '../../shared/services/apis/platform/category.service';
import { MatDialog } from '@angular/material/dialog';
import { StatusVersionEnum } from '../../shared/services/enums/status-version.enum';
import { AgreementModalComponent } from '../agreement-modal/agreement-modal.component';
import { LoginValidationComponent } from '../login-validation.component/login-validation.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CategoryStruct } from '../../shared/services/structs/category.struct';
import { RedirectService } from '../../redirect-service';

interface CategoryOptionItem {
    isHeader: boolean;
    label: string;
    idCategory?: number;
    classKey?: number;
}

@Component({
    selector: 'app-book-details',
    templateUrl: './book-details.component.html',
    styleUrls: ['./book-details.component.css'],
    standalone: true,
    imports: [CommonModule,
        LoginValidationComponent,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MenuComponent,
        MatOptionModule,
        MatRadioModule,
        FooterComponent,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatSelectModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class BookDetailsComponent implements OnInit {
    idBookVersion: number;
    bookVersion: BookVersionStruct | null = null;
    public model: FormGroup;
    public listCategories: CategoryStruct[] = [];
    authorPhotoPreview: string | null = null;
    public isLoading: boolean = false;
    private allOptions: CategoryOptionItem[] = [];
    public primaryOptions: CategoryOptionItem[] = [];
    public secondaryOptions: CategoryOptionItem[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private bookVersionService: BookVersionService,
        private categoryService: CategoryService,
        private bookDetailsService: BookDetailsService,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private redirectService: RedirectService,
        private dialog: MatDialog,
        public cdr: ChangeDetectorRef,
        private zone: NgZone,
    ) { }

    ngOnInit(): void {
        this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
        this.getCategory();
        this.model = this.formBuilder.group({
            bookTitle: ['', Validators.required],
            bookSubtitle: [''],
            versionName: ['', Validators.required],
            collectionName: [''],
            volumeName: [''],
            preface: ['', Validators.maxLength(250)],
            synopsis: ['', [Validators.required, Validators.minLength(500), Validators.maxLength(800)]],

            authors: this.formBuilder.array([
                this.formBuilder.control('')
            ]),

            authorPhotoBase64: [''],
            idPhotoShape: [1], // 1 = 'square' ou 2 = 'round', default square
            authorBio: ['', [Validators.required, Validators.maxLength(4000)]],

            primaryCategory: [null, Validators.required],
            secondaryCategory: [null, Validators.required],

            // language: ['', Validators.required],
            // ageRange: ['']
        });
        this.model.get('primaryCategory')!.valueChanges.subscribe(() => {
            this.refreshPrimaryAndSecondaryOptions();
        });
        this.model.get('secondaryCategory')!.valueChanges.subscribe(() => {
            this.refreshPrimaryAndSecondaryOptions();
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });

    }

    onLoginValidated(isLogged: boolean) {
        if (!isLogged) {
            return;
        }
        this.getBookVersion();
    }

    get authors() {
        return this.model.get('authors') as FormArray;
    }

    handlePhotoFile(file: File) {
        const reader = new FileReader();

        reader.onload = () => {
            // Garante execução dentro da zona do Angular
            this.zone.run(() => {
                const dataUrl = reader.result as string;

                // Atualiza preview e form
                this.authorPhotoPreview = dataUrl;
                this.model.patchValue({ authorPhotoBase64: dataUrl });

                // Força CD com OnPush
                this.cdr.markForCheck();
            });
        };

        reader.onerror = () => {
            this.zone.run(() => {
                this.alertService.show('Erro', 'Falha ao ler a imagem do autor.', AlertType.error);
                this.cdr.markForCheck();
            });
        };

        reader.readAsDataURL(file);
    }

    // (Opcional) pequenas proteções extra:
    onAuthorPhotoDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.dataTransfer?.files?.[0]) return;
        this.handlePhotoFile(event.dataTransfer.files[0]);
    }

    onAuthorPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files?.[0]) return;
        this.handlePhotoFile(input.files[0]);
    }

    private buildAllOptionsSorted(): void {
        // Agrupa por classe e ordena UMA VEZ
        const byClass = new Map<number, { className: string; items: CategoryStruct[] }>();
        for (const c of this.listCategories) {
            if (!byClass.has(c.idCategoryClass)) {
                byClass.set(c.idCategoryClass, { className: c.categoryClassName, items: [] });
            }
            byClass.get(c.idCategoryClass)!.items.push(c);
        }

        const classes = [...byClass.entries()]
            .map(([classKey, v]) => ({
                classKey,
                className: v.className,
                items: v.items.sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'pt-BR')),
            }))
            .sort((a, b) => a.className.localeCompare(b.className, 'pt-BR'));

        const result: CategoryOptionItem[] = [];
        for (const g of classes) {
            result.push({ isHeader: true, label: g.className, classKey: g.classKey });
            for (const it of g.items) {
                result.push({
                    isHeader: false,
                    label: it.categoryName,
                    idCategory: it.idCategory,
                    classKey: g.classKey
                });
            }
        }
        this.allOptions = result;
    }

    private buildFilteredOptions(excludeId: number | null): CategoryOptionItem[] {
        // Filtra itens; mantém headers apenas quando houver pelo menos 1 item abaixo
        const out: CategoryOptionItem[] = [];
        let currentHeaderIndex: number | null = null;
        let currentHeaderHasItems = false;

        const flushEmptyHeaderIfNeeded = () => {
            if (currentHeaderIndex != null && !currentHeaderHasItems) {
                // remove header sem itens
                out.splice(currentHeaderIndex, 1);
            }
            currentHeaderIndex = null;
            currentHeaderHasItems = false;
        };

        for (const opt of this.allOptions) {
            if (opt.isHeader) {
                // fechar o grupo anterior
                flushEmptyHeaderIfNeeded();
                // abrir novo grupo
                out.push(opt);
                currentHeaderIndex = out.length - 1;
                currentHeaderHasItems = false;
            } else {
                if (excludeId && opt.idCategory === Number(excludeId)) {
                    continue; // exclui a categoria selecionada no outro select
                }
                out.push(opt);
                currentHeaderHasItems = true;
            }
        }
        // flush final
        flushEmptyHeaderIfNeeded();
        return out;
    }

    private refreshPrimaryAndSecondaryOptions(): void {
        const primary = this.model?.get('primaryCategory')?.value ?? null;
        const secondary = this.model?.get('secondaryCategory')?.value ?? null;
        this.primaryOptions = this.buildFilteredOptions(secondary ? Number(secondary) : null);
        this.secondaryOptions = this.buildFilteredOptions(primary ? Number(primary) : null);
        this.cdr.markForCheck();
    }


    onAuthorPhotoDragOver(event: DragEvent) {
        event.preventDefault();
    }

    toggleidPhotoShape(value: string) {
        this.model.patchValue({ idPhotoShape: value });
    }

    addAuthor() {
        this.authors.push(this.formBuilder.control('', Validators.required));
    }

    removeAuthor(index: number) {
        if (this.authors.length > 1) this.authors.removeAt(index);
    }

    get filteredPrimaryCategories() {
        const secondary = this.model?.get('secondaryCategory')?.value;
        return this.listCategories.filter(cat => cat.idCategory !== secondary);
    }
    get filteredSecondaryCategories() {
        const primary = this.model?.get('primaryCategory')?.value;
        return this.listCategories.filter(cat => cat.idCategory !== primary);
    }

    getBookVersion(): void {
        this.isLoading = true;
        this.bookVersionService.GetBookVersion(this.idBookVersion, StatusVersionEnum.DadosDoLivro).subscribe({
            next: (res: BookVersionResponse) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription, AlertType.error);
                    return;
                }
                this.bookVersion = res.bookVersionStruct;
                this.redirectService.redirectByStatus(res.bookVersionStruct.idStatusVersion, this.idBookVersion, res.bookVersionStruct.isCreditReviewing);

                // Popula os campos do formulário com o retorno
                this.model.patchValue({
                    bookTitle: this.bookVersion.bookTitle || '',
                    bookSubtitle: this.bookVersion.bookSubtitle || '',
                    versionName: this.bookVersion.versionName || '',
                    collectionName: this.bookVersion.collectionName || '',
                    volumeName: this.bookVersion.volumeName || '',
                    synopsis: this.bookVersion.synopsis || '',
                    preface: this.bookVersion.preface || '',
                    primaryCategory: this.bookVersion.idPrimaryCategory ? Number(this.bookVersion.idPrimaryCategory) : null,
                    secondaryCategory: this.bookVersion.idSecondaryCategory ? Number(this.bookVersion.idSecondaryCategory) : null,
                    idPhotoShape: this.bookVersion.idPhotoShape ? Number(this.bookVersion.idPhotoShape) : 1,
                    authorBio: this.bookVersion.authorBio || ''
                });

                this.refreshPrimaryAndSecondaryOptions();

                // Popula a foto do autor, se existir base64
                if (this.bookVersion.authorPhotoBase64) {
                    this.authorPhotoPreview = `data:image/jpeg;base64,${this.bookVersion.authorPhotoBase64}`;
                    this.model.patchValue({ authorPhotoBase64: this.bookVersion.authorPhotoBase64 });
                } else {
                    this.authorPhotoPreview = null;
                    this.model.patchValue({ authorPhotoBase64: '' });
                }

                // Popula autores se vierem preenchidos
                if (this.bookVersion.listBookAuthorsStruct && this.bookVersion.listBookAuthorsStruct.length > 0) {
                    // Limpa o FormArray atual e adiciona os autores do backend
                    const authorsArray = this.authors;
                    while (authorsArray.length !== 0) {
                        authorsArray.removeAt(0);
                    }
                    this.bookVersion.listBookAuthorsStruct.forEach(a => {
                        authorsArray.push(this.formBuilder.control(a.authorName, Validators.required));
                    });
                }

                this.cdr.markForCheck(); // Atualiza a view

                this.isLoading = false;
            },
            error: err => {
                this.alertService.show('Erro inesperado', err, AlertType.error);
                this.isLoading = false;
            }
        });
    }


    getCategory() {
        this.categoryService.getCategory().subscribe({
            next: (response) => {
                if (!response.isError && response.listCategoryStruct && response.listCategoryStruct.length > 0) {
                    this.listCategories = response.listCategoryStruct;
                    this.buildAllOptionsSorted();
                    this.refreshPrimaryAndSecondaryOptions();
                    this.cdr.markForCheck();

                } else {
                    this.alertService.show('Aviso', 'Nenhuma categoria encontrada.', AlertType.warning);
                }
                this.cdr.markForCheck();
            },
            error: (err) => {
                this.alertService.show('Erro', 'Erro ao carregar categorias. Tente novamente mais tarde.', AlertType.error);
            }
        });
    }

    trackByOption = (_: number, item: CategoryOptionItem) =>
        item.isHeader ? `h-${item.classKey}-${item.label}` : `i-${item.idCategory}`;


    onCategoryChange(controlName: 'primaryCategory' | 'secondaryCategory', value: any) {
        this.model.get(controlName)?.setValue(value !== null ? Number(value) : null);
    }

    voltar() {
        this.router.navigate(['/livro-impressao', this.idBookVersion]);
    }

    async onSubmit() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (this.model.invalid) {
            this.model.markAllAsTouched();
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.isLoading = true;

        // 1. Abrir o modal de acordo antes de prosseguir
        const dialogRef = this.dialog.open(AgreementModalComponent, {
            width: '90%',
            maxHeight: '90vh',
            data: StatusVersionEnum.DadosDoLivro
        });

        const acceptedAgreements: AgreementStruct[] | null = await dialogRef.afterClosed().toPromise();
        // 2. Cancelado ou não aceitou
        if (!acceptedAgreements) {
            this.alertService.show('Erro', 'Você precisa aceitar os termos obrigatórios para continuar.', AlertType.error);
            return;
        }

        // 3. Monta o request
        const authorPhotoBase64Raw = this.model.value.authorPhotoBase64;
        const authorPhotoBase64 = authorPhotoBase64Raw
            ? authorPhotoBase64Raw.replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            : '';

        const request: BookDetailsRequest = {
            idBookVersion: this.idBookVersion,
            bookTitle: this.model.value.bookTitle,
            bookSubtitle: this.model.value.bookSubtitle,
            versionName: this.model.value.versionName,
            collectionName: this.model.value.collectionName,
            volumeName: this.model.value.volumeName,
            synopsis: this.model.value.synopsis,
            preface: this.model.value.preface,
            idPrimaryCategory: Number(this.model.value.primaryCategory),
            idSecondaryCategory: Number(this.model.value.secondaryCategory),
            authorPhotoBase64: authorPhotoBase64,
            idPhotoShape: Number(this.model.value.idPhotoShape),
            authorBio: this.model.value.authorBio,
            listAuthors: this.model.value.authors,
            listAgreementStruct: new Array<AgreementStruct>(), // aqui, agreements do modal
        };

        // 4. Executa submit normalmente
        this.bookDetailsService.post(request).subscribe({
            next: (response) => {
                if (response.isError) {
                    this.alertService.show('Erro', 'Erro ao salvar detalhes do livro: ' + response.errorDescription, AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Detalhes do livro salvos com sucesso!', AlertType.success);
                this.router.navigate(['/livro-status', this.idBookVersion]);
                this.isLoading = false;
            },
            error: (err) => {
                this.alertService.show('Erro', 'Erro ao salvar detalhes do livro. Tente novamente mais tarde.', AlertType.error);
                this.isLoading = false;
            }
        });
    }
}
